import { useQuery } from 'react-query'
import { gridClient } from '@/utils/apiUtil'
import { CustomerModules } from '@/utils/moduleUtil'
import type { ViewAsCapability } from '@/api/view-as'
import type { FreeTrialType } from '@/lib/constants'

type Role = {
  id: string;
  name: string;
  description: string;
  isVisible: boolean;
};

type Product = {
  id: string;
  customerId: string;
  productId: string;
};

type IPAddress = {
  id: number;
  customerId: string;
  address: string;
  createdDateTime: string;
  isActive: boolean;
  environment: string;
};

type Workflow = {
  id: string;
  name: string;
  status: string;
  customerId: string;
  attributes: string[];
  standaloneLinkEnabled: boolean;
  createdDateTime: string;
  deletedDateTime: string | null;
  applyId: string | null;
  // Sub-Organizations: set on the workflow catalogue when a workflow is
  // assigned to a sub-organization. `assignedTo` is the sub-org's name.
  isAssigned?: boolean;
  assignedTo?: string | null;
};

// `CustomerModules` lives in `moduleUtil`, together with the rule for reading a flag
// off it: the object carries a price next to every flag, and only that rule can tell
// the two apart.

/**
 * The free-trial slot the customer is enrolled into, plus the campaign that
 * granted it. Shapes follow the "Grid: Campaigns" Postman collection.
 */
type FreeTrialCampaign = {
  name?: string;
  displayName?: string;
  isActive?: boolean;
  limit?: number;
  verifications?: number | null;
  trialType?: FreeTrialType;
  // Head-to-head term length in days. Null for standard campaigns.
  trialDays?: number | null;
  startDateTime?: string | null;
  endDateTime?: string | null;
};

export type FreeTrial = {
  id?: string;
  // Verifications granted by the campaign, before any are spent. Null is unlimited.
  verifications?: number | null;
  // Transactions already charged against the slot.
  transactionIds?: string[];
  enrolledDateTime?: string | null;
  expiresDateTime?: string | null;
  allowedFcraProductIds?: string[];
  campaign?: FreeTrialCampaign;
};

type Customer = {
  id: string;
  name: string;
  size: string | null;
  industry: string | null;
  dba: string | null;
  hasBeenInitialized: boolean;
  billingEmail: string | null;
  canOverride: boolean;
  logo: string;
  favicon: string;
  homePageUrl: string;
  hasSignedContract: boolean;
  deletedDateTime: string | null;
  subscriberCodes: string[];
  workflows: Workflow[];
  ipAddresses: IPAddress[];
  modules?: CustomerModules;
  freeTrial?: FreeTrial;
  users: {
    id: string;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    suffix: string | null;
    companyRole: string | null;
    email: string;
    createdDateTime: string;
    updatedDateTime: string | null;
    deletedDateTime: string | null;
    customerId: string;
    roleId: string;
    hasViewedTutorial: boolean;
    authenticationMethod: string[];
    theme: string;
    acceptedTerms: boolean;
    acceptedTermsDateTime: string | null;
    role: Role;
  }[];
  products: Product[];
};

type User = {
  id: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  companyRole: string | null;
  email: string;
  createdDateTime: string;
  updatedDateTime: string | null;
  deletedDateTime: string | null;
  customerId: string;
  roleId: string;
  hasViewedTutorial: boolean;
  authenticationMethod: string[];
  theme: string;
  language?: string | null;
  acceptedTerms: boolean;
  acceptedTermsDateTime: string | null;
  customer: Customer;
  role: Role;
  viewAs?: ViewAsCapability;
  homeCustomer?: {
    id: string;
    name: string;
  };
}

export const getUserFromIdRequest = async (userId: string) => {
  const { data } = await gridClient.get<User>(`/user/${userId}`)
  return data
}

export function getUserFromIdHook(userId) {
  return useQuery(['get-solutions', userId], () => getUserFromIdRequest(userId), { refetchOnWindowFocus: false, enabled: false })
}
